import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Loader2, Mail, Lock, User, CheckCircle2, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot' | 'pending' | 'change_password'
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [forgotSent, setForgotSent] = useState(false);
  const [pwChange, setPwChange] = useState({ current: '', newPw: '', confirm: '' });
  const [tempToken, setTempToken] = useState(null);
  const [authError, setAuthError] = useState(''); // Iter 68 C5: inline error
  // 2026-06-09 — Refs let us read the *DOM* values on submit. Browser
  // autofill and Playwright's `fill()` can populate `input.value` before
  // React's controlled `onChange` has fired, leading to a stale empty
  // string in `form.email/password` at submit time → first-attempt 401.
  // Reading from the refs as a fallback closes that race.
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError(''); // clear previous
    // 2026-06-09 — Use the DOM value as the source of truth on submit,
    // then sync React state. This protects against the autofill race
    // (Chrome / 1Password / Playwright fill + immediate submit).
    const liveEmail = (emailRef.current?.value ?? form.email).trim();
    const livePassword = passwordRef.current?.value ?? form.password;
    if (liveEmail !== form.email || livePassword !== form.password) {
      setForm(f => ({ ...f, email: liveEmail, password: livePassword }));
    }
    if (!liveEmail || (mode !== 'forgot' && !livePassword)) return toast.error('Please fill in all fields');
    if (mode === 'signup' && !form.name) return toast.error('Please enter your name');
    setLoading(true);

    try {
      if (mode === 'login') {
        const res = await authAPI.login({ email: liveEmail, password: livePassword });
        if (res.data.must_change_password) {
          // DON'T login yet — show password change screen first
          setTempToken(res.data.token);
          localStorage.setItem('stellar_token', res.data.token);
          setPwChange({ current: livePassword, newPw: '', confirm: '' });
          setMode('change_password');
          toast.info('Please create a new password to continue');
        } else {
          login(res.data.token, res.data.user);
          toast.success(`Welcome back, ${res.data.user.name}!`);
        }
      } else if (mode === 'signup') {
        const res = await authAPI.register({ name: form.name, email: liveEmail, password: livePassword });
        if (res.data.status === 'pending') {
          setMode('pending');
        } else {
          login(res.data.token, res.data.user);
          toast.success('Welcome to Stellar CRM!');
        }
      } else if (mode === 'forgot') {
        await authAPI.forgotPassword({ email: liveEmail });
        setForgotSent(true);
        toast.success('Reset link sent! Check your email.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const status = err?.response?.status;
      if (status === 403 && detail.includes('pending')) {
        setMode('pending');
      } else {
        // 2026-08 P0 — distinguish credential errors from service failures.
        let friendly;
        if (!err?.response || status >= 500 || status === 503) {
          // Network failure / backend down / DB unavailable — NOT a password problem.
          friendly = "We're unable to sign you in right now. Please try again shortly.";
        } else if (status === 429) {
          friendly = 'Too many attempts — please wait a minute and try again.';
        } else if (status === 401 || /invalid|incorrect|wrong|not found/i.test(detail)) {
          // Genuinely wrong credentials (generalised so we don't enumerate accounts).
          friendly = 'Incorrect email/ID or password.';
        } else {
          friendly = detail || 'Something went wrong. Please try again.';
        }
        if (mode === 'login') setAuthError(friendly);
        toast.error(friendly);
      }
    } finally {
      setLoading(false);
    }
  };

  // Pending approval screen
  if (mode === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="pending-approval-screen">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
            <h2 className="text-xl font-display font-semibold mb-2">Account Pending Approval</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Thank you for signing up! Your account is awaiting admin approval. 
              You will receive an email notification once your account has been approved.
            </p>
            <div className="rounded-lg bg-secondary/50 p-3 text-xs text-muted-foreground mb-6">
              <p className="flex items-center gap-2 justify-center">
                <Mail className="h-3.5 w-3.5" />
                We'll notify you at <strong className="text-foreground">{form.email}</strong>
              </p>
            </div>
            <Button variant="outline" onClick={() => setMode('login')} data-testid="back-to-login-button">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Force password change screen (for invited users with temp password)
  if (mode === 'change_password') {
    const handlePwChange = async (e) => {
      e.preventDefault();
      if (!pwChange.newPw || pwChange.newPw.length < 6) return toast.error('Password must be at least 6 characters');
      if (pwChange.newPw !== pwChange.confirm) return toast.error('Passwords do not match');
      setLoading(true);
      try {
        await authAPI.changePassword({ current_password: pwChange.current, new_password: pwChange.newPw });
        // Now log in with new password
        const res = await authAPI.login({ email: form.email, password: pwChange.newPw });
        login(res.data.token, res.data.user);
        toast.success('Password updated! Welcome to Stellar CRM.');
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to change password');
      }
      setLoading(false);
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="change-password-screen">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-gold" />
              </div>
              <h2 className="text-xl font-display font-semibold mb-2">Create Your Password</h2>
              <p className="text-sm text-muted-foreground">
                Welcome! You're logging in with a temporary password. Please create your own secure password to continue.
              </p>
            </div>
            <form onSubmit={handlePwChange} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[13px]">New Password</Label>
                <PasswordInput placeholder="Create a strong password (min 6 chars)" value={pwChange.newPw} onChange={e => setPwChange(p => ({ ...p, newPw: e.target.value }))} className="h-11 text-[14px]" data-testid="new-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px]">Confirm Password</Label>
                <PasswordInput placeholder="Type your new password again" value={pwChange.confirm} onChange={e => setPwChange(p => ({ ...p, confirm: e.target.value }))} className="h-11 text-[14px]" data-testid="confirm-password-input" />
              </div>
              <Button type="submit" className="w-full h-11 text-[14px] bg-gold/90 hover:bg-gold text-black font-semibold" disabled={loading} data-testid="change-password-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Password & Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password - sent confirmation
  if (mode === 'forgot' && forgotSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="forgot-sent-screen">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-display font-semibold mb-2">Check Your Email</h2>
            <p className="text-sm text-muted-foreground mb-6">
              If an account exists for <strong className="text-foreground">{form.email}</strong>, we've sent a password reset link. Please check your inbox (and spam folder).
            </p>
            <Button variant="outline" onClick={() => { setMode('login'); setForgotSent(false); }} data-testid="back-to-login-from-forgot">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="login-page">
      <Card className="w-full max-w-md bg-card/80 backdrop-blur border-border/50">
        <CardHeader className="text-center pb-2">
          <img
            src="/stp-logo.png"
            alt="Stellar Productions"
            className="h-16 w-16 object-contain mx-auto mb-2"
          />
          <CardTitle className="text-xl font-display">
            {mode === 'login' ? 'Welcome Back' : mode === 'signup' ? 'Get Started' : 'Reset Password'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {mode === 'login' ? 'Log in to your Stellar CRM account' : mode === 'signup' ? 'Create your account to request access' : 'Enter your email to receive a reset link'}
          </p>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="name" placeholder="Your full name" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="pl-9" data-testid="name-input" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete={mode === 'signup' ? 'email' : 'username'}
                  autoFocus
                  ref={emailRef}
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="pl-9"
                  data-testid="email-input"
                />
              </div>
            </div>
            {mode !== 'forgot' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === 'login' && (
                    <button type="button" onClick={() => setMode('forgot')}
                      className="text-xs text-gold hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <PasswordInput
                    id="password"
                    ref={passwordRef}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    placeholder={mode === 'login' ? 'Your password' : 'Create a password (min 6 chars)'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="pl-9"
                    data-testid="password-input"
                  />
                </div>
              </div>
            )}

            {mode === 'signup' && (
              <div className="rounded-lg bg-secondary/50 p-2.5 text-[11px] text-muted-foreground flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-gold shrink-0" />
                <span>After signing up, your account will be reviewed by an admin. You'll receive an email once approved.</span>
              </div>
            )}

            {/* Iter 68 C5: inline visible auth error so users always see why login failed (not just a toast). */}
            {mode === 'login' && authError && (
              <div
                className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[12px] text-red-300"
                role="alert"
                data-testid="login-error"
              >
                {authError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === 'login' ? 'Log In' : mode === 'signup' ? 'Sign Up' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm">
            {mode === 'login' && (
              <p className="text-muted-foreground">
                Don't have an account?{' '}
                <button onClick={() => setMode('signup')} className="text-gold hover:underline font-medium" data-testid="switch-to-signup">Sign up</button>
              </p>
            )}
            {mode === 'signup' && (
              <p className="text-muted-foreground">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-gold hover:underline font-medium" data-testid="switch-to-login">Log in</button>
              </p>
            )}
            {mode === 'forgot' && (
              <p className="text-muted-foreground">
                Remember your password?{' '}
                <button onClick={() => setMode('login')} className="text-gold hover:underline font-medium" data-testid="back-to-login-link">Back to login</button>
              </p>
            )}
          </div>
          <div className="mt-3 text-center">
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-gold transition-colors" data-testid="privacy-policy-link">
              Privacy Policy
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
